import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { change, Field, reduxForm } from 'redux-form';
import { Table, Card, CardBody } from 'reactstrap';
import { Fab, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory';
import ScaleIcon from '@mui/icons-material/Scale';
import StraightenIcon from '@mui/icons-material/Straighten';
import DescriptionIcon from '@mui/icons-material/Description';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { connect } from 'react-redux';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import renderField from 'src/shared/components/form/renderField';

const OrderItemAddorUpdate = ({ setItemDetails, itemDetails, isOrderItemDetails, dispatch, isId, setOrderItemDetails, isInfo }) => {

    useEffect(() => {
        if (isId) {
            getOrderItemWiseDetails(isId);
        }
    }, [isId]);

    const handleAddItem = (index) => {
        const newItemDetails = [...itemDetails.slice(0, index + 1), { id: itemDetails.length + 1 }, ...itemDetails.slice(index + 1)];
        setItemDetails(newItemDetails);
    };

    const handleDeleteItem = (id, index) => {
        const updatedItemDetails = itemDetails.filter(item => item.id !== id);
        setItemDetails(updatedItemDetails);
        if (index < itemDetails.length - 1) {
            handleAddItem(index);
        }
    };

    const handleDeleteItemUpdate = (id, index) => {
        const updatedItemDetails = isOrderItemDetails.filter(item => item.id !== id);
        setOrderItemDetails(updatedItemDetails);
        if (index < isOrderItemDetails.length - 1) {
            handleDeleteItem(index);
        }
    };

    /** Update for Order */
    const handleUpdateItem = (index) => {
        const newItemDetails = [...isOrderItemDetails.slice(0, index + 1), { id: isOrderItemDetails.length + 1 }, ...isOrderItemDetails.slice(index + 1)];
        setOrderItemDetails(newItemDetails);
    };

    const getOrderItemWiseDetails = async (orderId) => {
        try {
            const response = await PndRestApi.GetOrderItemWiseDetails(orderId);
            if (response.status === 200 && response.data.status === 200) {
                const items = response.data.data.map((item, index) => ({
                    id: index + 1,
                    noOfItems: item.noOfItems,
                    boxBarCode: item.boxBarCode,
                    weightKg: item.weightKg,
                    boxDimension: item.boxDimension,
                    boxDescription: item.boxDescription,
                }));

                items.forEach(item => {
                    dispatch(change('orderForm', `boxBarCode_${item.id}`, item.boxBarCode));
                    dispatch(change('orderForm', `numberOfBox_${item.id}`, item.noOfItems));
                    dispatch(change('orderForm', `weightKg_${item.id}`, item.weightKg));
                    dispatch(change('orderForm', `boxDescription_${item.id}`, item.boxDescription));
                    if (item.boxDimension) {
                        const dimensions = item.boxDimension.split('X');
                        const length = dimensions[0];
                        const width = dimensions[1];
                        const height = dimensions[2];
                        dispatch(change('orderForm', `length_${item.id}`, length));
                        dispatch(change('orderForm', `width_${item.id}`, width));
                        dispatch(change('orderForm', `height_${item.id}`, height));
                    }
                });
                setOrderItemDetails(items);

            } else {
                showNotification(
                    "theme-light",
                    "ltr",
                    `SuccessCode: ${response.data.status}`,
                    `Message:Item detail is not available.`,
                );
                return;
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification(
                "theme-light",
                "ltr",
                `SuccessCode: 500`,
                ERROR_MESSAGES.tryCatchError,
            );
        }
    };

    return (
        <Card className="border-0 shadow-sm mb-3">
            <CardBody className="p-3">
                <div className="d-flex align-items-center mb-3 text-primary border-bottom pb-2">
                    <InventoryIcon style={{ fontSize: '1.2rem' }} className="me-2" />
                    <h6 className="m-0 fw-bold small text-uppercase">Order Item Details</h6>
                </div>
                <div className="table-responsive">
                    <Table className="table-sm table-nowrap align-middle" borderless>
                        <thead className="bg-light">
                            {!isInfo && itemDetails && !isOrderItemDetails ?
                                <tr>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "12%" }}>ITEMS QTY <span className='text-danger'>*</span></th>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "10%" }}>WEIGHT (KG)</th>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "10%" }}>LENGTH (CM)</th>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "10%" }}>WIDTH (CM)</th>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "10%" }}>HEIGHT (CM)</th>
                                    <th className="small fw-bold text-muted py-2">DESCRIPTION</th>
                                    <th className="text-center py-2" style={{ width: "60px" }}></th>
                                </tr> :
                                <tr>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "15%" }}>BARCODE</th>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "10%" }}>ITEMS QTY <span className='text-danger'>*</span></th>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "10%" }}>WEIGHT (KG)</th>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "10%" }}>LENGTH (CM)</th>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "10%" }}>WIDTH (CM)</th>
                                    <th className="small fw-bold text-muted py-2" style={{ width: "10%" }}>HEIGHT (CM)</th>
                                    <th className="small fw-bold text-muted py-2">DESCRIPTION</th>
                                    <th className="text-center py-2" style={{ width: "60px" }}></th>
                                </tr>
                            }
                        </thead>
                        <tbody>
                            {!isInfo && itemDetails && !isOrderItemDetails ? itemDetails.map((item, index) => (
                                <tr key={index}>
                                    <td>
                                        <Field name={`numberOfBox_${item.id}`} component={renderField} type="text" placeholder="Qty" icon={InventoryIcon} />
                                    </td>
                                    <td>
                                        <Field name={`weightKg_${item.id}`} component={renderField} type="text" placeholder="Kg" icon={ScaleIcon} />
                                    </td>
                                    <td>
                                        <Field name={`length_${item.id}`} component={renderField} type="text" placeholder="L" icon={StraightenIcon} />
                                    </td>
                                    <td>
                                        <Field name={`width_${item.id}`} component={renderField} type="text" placeholder="W" icon={StraightenIcon} />
                                    </td>
                                    <td>
                                        <Field name={`height_${item.id}`} component={renderField} type="text" placeholder="H" icon={StraightenIcon} />
                                    </td>
                                    <td>
                                        <Field name={`boxDescription_${item.id}`} component={renderField} type="textarea" placeholder="Item Descriptions" icon={DescriptionIcon} />
                                    </td>
                                    <td className="text-center">
                                        {index === 0 ? (
                                            <Tooltip title="Add New Item">
                                                <Fab size="small" color="success" aria-label="add"
                                                    style={{ width: '30px', height: '30px', minHeight: '30px', backgroundColor: '#32CA73' }}
                                                    onClick={() => handleAddItem(itemDetails.length - 1)}>
                                                    <AddIcon style={{ fontSize: '1rem' }} />
                                                </Fab>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Delete Item">
                                                <Fab size="small" color="secondary" aria-label="delete"
                                                    style={{ width: '30px', height: '30px', minHeight: '30px' }}
                                                    onClick={() => handleDeleteItem(item.id, index)}>
                                                    <DeleteIcon style={{ fontSize: '1rem' }} />
                                                </Fab>
                                            </Tooltip>
                                        )}
                                    </td>
                                </tr>
                            )) : isOrderItemDetails && isOrderItemDetails.map((item, index) => (
                                <tr key={index}>
                                    <td>
                                        <Field name={`boxBarCode_${item.id}`} component={renderField} type="text" placeholder="Barcode" disabled={true} icon={QrCodeIcon} />
                                    </td>
                                    <td>
                                        <Field name={`numberOfBox_${item.id}`} component={renderField} type="text" placeholder="Qty" icon={InventoryIcon} />
                                    </td>
                                    <td>
                                        <Field name={`weightKg_${item.id}`} component={renderField} type="text" placeholder="Kg" icon={ScaleIcon} />
                                    </td>
                                    <td>
                                        <Field name={`length_${item.id}`} component={renderField} type="text" placeholder="L" icon={StraightenIcon} />
                                    </td>
                                    <td>
                                        <Field name={`width_${item.id}`} component={renderField} type="text" placeholder="W" icon={StraightenIcon} />
                                    </td>
                                    <td>
                                        <Field name={`height_${item.id}`} component={renderField} type="text" placeholder="H" icon={StraightenIcon} />
                                    </td>
                                    <td>
                                        <Field name={`boxDescription_${item.id}`} component={renderField} type="textarea" placeholder="Item Descriptions" icon={DescriptionIcon} />
                                    </td>
                                    <td className="text-center">
                                        {index === 0 ? (
                                            <Tooltip title="Add New Item">
                                                <Fab size="small" color="success" aria-label="add"
                                                    style={{ width: '30px', height: '30px', minHeight: '30px', backgroundColor: '#32CA73' }}
                                                    onClick={() => handleUpdateItem(isOrderItemDetails.length - 1)}>
                                                    <AddIcon style={{ fontSize: '1rem' }} />
                                                </Fab>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Delete Item">
                                                <Fab size="small" color="secondary" aria-label="delete"
                                                    style={{ width: '30px', height: '30px', minHeight: '30px' }}
                                                    onClick={() => handleDeleteItemUpdate(item.id, index)}>
                                                    <DeleteIcon style={{ fontSize: '1rem' }} />
                                                </Fab>
                                            </Tooltip>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </CardBody>
        </Card>
    );
};

OrderItemAddorUpdate.propTypes = {
    previousPage: PropTypes.func,
    setItemDetails: PropTypes.func.isRequired,
    itemDetails: PropTypes.array.isRequired,
    isOrderItemDetails: PropTypes.array,
    setOrderItemDetails: PropTypes.func,
    isId: PropTypes.string,
    isInfo: PropTypes.object,
    dispatch: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => {
    const { data } = ownProps;
    if (data && data.data) {
        return {
            initialValues: {
                boxBarCode: data.data.boxBarCode,
                noOfItems: data.data.noOfItems,
                weightKg: data.data.weightKg,
                length: data.data.length,
                width: data.data.width,
                height: data.data.height,
                boxDescription: data.data.boxDescription
            }
        };
    }
    return {};
};

export default connect(mapStateToProps)(
    reduxForm({
        form: "orderForm",
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: false,
        enableReinitialize: false,
    })(OrderItemAddorUpdate)
);